import { ChangeEvent, useMemo, useState } from "react";
import { attendanceCheckProblems } from "../lib/attendanceCheck";
import { supabase } from "../lib/supabase";
import type { Attendance, Person, Project } from "../types";

type SummaryRow = Person & {
  regular: number;
  overtime: number;
  days: number;
  payable: number;
};

type AiRow = {
  name: string;
  work_date: string;
  area: string;
  regular_hours: number;
  overtime_hours: number;
  confidence: number;
  note: string;
};

type AiPersonRow = {
  name: string;
  trade: string;
  phone: string;
  id_no: string;
  bank_card: string;
  bank_name: string;
  remark: string;
  confidence: number;
};

type AiSalaryRow = {
  name: string;
  salary_type: "日薪" | "月薪";
  rate: number;
  overtime_rate: number;
  confidence: number;
  note: string;
};

type CrossConflict = {
  key: string;
  name: string;
  work_date: string;
  total_regular: number;
  entries: Array<{
    attendance_id: string;
    project_id: string;
    project_name: string;
    regular_hours: number;
    overtime_hours: number;
  }>;
};

type AiMode =
  | "home"
  | "screenshot"
  | "rules"
  | "smart"
  | "people"
  | "salary"
  | "audit"
  | "analysis";

type Props = {
  project?: Project;
  date: string;
  setDate: (value: string) => void;
  people: Person[];
  attendance: Attendance[];
  summary: SummaryRow[];
  reload: () => Promise<void>;
  notify: (text: string, bad?: boolean) => void;
};

async function aiRequest<T>(body: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("登录已过期，请退出后重新登录");
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 150000);
  try {
    const response = await fetch("/api/ai", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const result = (await response.json().catch(() => ({}))) as T & {
      error?: string;
    };
    if (!response.ok)
      throw new Error(result.error || `AI 服务请求失败（${response.status}）`);
    return result;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw new Error("AI 识别超时，请缩小截图后重试");
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function crossProjectRequest<T>(body: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("登录已过期，请重新登录");
  const response = await fetch("/api/cross-project-audit", {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const result = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(result.error || "跨项目核对失败");
  return result;
}

function fileDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("截图读取失败"));
    reader.readAsDataURL(file);
  });
}

async function prepareImage(file: File) {
  if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type))
    throw new Error("仅支持 PNG、JPG、WebP 图片");
  if (file.size > 12 * 1024 * 1024) throw new Error("图片不能超过 12MB");
  const source = await fileDataUrl(file);
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("无法解析这张图片"));
    image.src = source;
  });
  // Phone screenshots can be several thousand pixels tall.  Keeping the long
  // edge modest makes the request much faster while preserving chat text.
  const maximum = 1400;
  const scale = Math.min(1, maximum / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("浏览器无法处理这张截图");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

function isCorrectionText(value: string) {
  return /^\s*(?:【\s*更正\s*】|更正\s*[:：]?)/.test(value || "");
}

export function AiAssistantView({
  project,
  date,
  setDate,
  people,
  attendance,
  summary,
  reload,
  notify,
}: Props) {
  const [sourceText, setSourceText] = useState("");
  const [image, setImage] = useState("");
  const [imageName, setImageName] = useState("");
  const [rows, setRows] = useState<AiRow[]>([]);
  const [personRows, setPersonRows] = useState<AiPersonRow[]>([]);
  const [salaryRows, setSalaryRows] = useState<AiSalaryRow[]>([]);
  const [peopleText, setPeopleText] = useState("");
  const [salaryText, setSalaryText] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [recognizeError, setRecognizeError] = useState("");
  const [running, setRunning] = useState("");
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [mode, setMode] = useState<AiMode>("home");
  const [draftSource, setDraftSource] = useState("");
  const [correctionMode, setCorrectionMode] = useState(false);
  const [crossConflicts, setCrossConflicts] = useState<CrossConflict[]>([]);
  const [crossChoices, setCrossChoices] = useState<Record<string, string>>({});

  const nameMap = useMemo(
    () => new Map(people.map((person) => [person.name, person])),
    [people],
  );

  const applyCrossConflicts = (conflicts: CrossConflict[]) => {
    setCrossConflicts(conflicts);
    setCrossChoices(Object.fromEntries(conflicts.map(conflict => [
      conflict.key,
      conflict.entries.find(entry => Number(entry.regular_hours) > 0)?.project_id || "",
    ])));
  };

  const checkCrossProjects = async (drafts: AiRow[] = []) => {
    if (!project) return [] as CrossConflict[];
    const grouped = new Map<string, AiRow>();
    for (const row of drafts) {
      const cleanName = row.name.replace(/\s+/g, "").trim();
      const area = row.area.trim();
      const key = `${cleanName}|${row.work_date}|${area}`;
      const current = grouped.get(key);
      if (current) {
        grouped.set(key, {
          ...current,
          area,
          regular_hours: correctionMode
            ? Number(row.regular_hours || 0)
            : Number(current.regular_hours || 0) + Number(row.regular_hours || 0),
          overtime_hours: correctionMode
            ? Number(row.overtime_hours || 0)
            : Number(current.overtime_hours || 0) + Number(row.overtime_hours || 0),
        });
        continue;
      }
      grouped.set(key, {
        ...row,
        name: cleanName,
        area,
      });
    }
    const touched = new Set([...grouped.values()].map(row => `${row.name}|${row.work_date}`));
    const projected = new Map<string, AiRow>();
    for (const existing of attendance) {
      const person = people.find(item => item.id === existing.person_id);
      if (!person) continue;
      const cleanName = person.name.replace(/\s+/g, "").trim();
      const dailyKey = `${cleanName}|${existing.work_date}`;
      if (!touched.has(dailyKey)) continue;
      const area = String(existing.area || "").trim();
      projected.set(`${dailyKey}|${area}`, {
        name: cleanName,
        work_date: existing.work_date,
        area,
        regular_hours: Number(existing.regular_hours || 0),
        overtime_hours: Number(existing.overtime_hours || 0),
        confidence: 1,
        note: "",
      });
    }
    for (const [key, row] of grouped) {
      const existing = projected.get(key);
      projected.set(key, {
        ...row,
        regular_hours: correctionMode
          ? Number(row.regular_hours || 0)
          : Number(existing?.regular_hours || 0) + Number(row.regular_hours || 0),
        overtime_hours: correctionMode
          ? Number(row.overtime_hours || 0)
          : Number(existing?.overtime_hours || 0) + Number(row.overtime_hours || 0),
      });
    }
    const result = await crossProjectRequest<{ conflicts: CrossConflict[] }>({
      action: "check",
      month: date.slice(0, 7),
      drafts: [...projected.values()].map(row => ({
        project_id: project.id,
        project_name: project.name,
        name: row.name.trim(),
        work_date: row.work_date,
        area: row.area,
        regular_hours: Number(row.regular_hours || 0),
        overtime_hours: Number(row.overtime_hours || 0),
      })),
    });
    applyCrossConflicts(result.conflicts || []);
    return result.conflicts || [];
  };

  const resolveCrossConflict = async (conflict: CrossConflict) => {
    const keepProjectId = crossChoices[conflict.key];
    const keepProject = conflict.entries.find(entry => entry.project_id === keepProjectId);
    if (!keepProjectId || !keepProject)
      return notify("请选择这天正班考勤应归属的项目", true);
    if (!window.confirm(`确认将 ${conflict.name} ${conflict.work_date} 的正班归属“${keepProject.project_name}”吗？\n其他项目中的正班将删除；如果同时有加班，会保留为仅加班记录。`)) return;
    setRunning(`resolve:${conflict.key}`);
    try {
      const result = await crossProjectRequest<{ updated: number; deleted: number }>({
        action: "resolve",
        month: conflict.work_date.slice(0, 7),
        name: conflict.name,
        work_date: conflict.work_date,
        keep_project_id: keepProjectId,
        drafts: project ? rows.map(row => ({
          project_id: project.id,
          project_name: project.name,
          name: row.name.trim(),
          work_date: row.work_date,
          area: row.area,
          regular_hours: Number(row.regular_hours || 0),
          overtime_hours: Number(row.overtime_hours || 0),
        })) : [],
      });
      notify(`已处理跨项目冲突：保留 ${keepProject.project_name}，修改 ${result.updated || 0} 条，删除 ${result.deleted || 0} 条`);
      await reload();
      let nextRows = rows;
      if (project && keepProjectId !== project.id && conflict.entries.some(entry => !entry.attendance_id && entry.project_id === project.id)) {
        const targetName = conflict.name.replace(/\s+/g, "");
        nextRows = rows.flatMap(row => {
          if (row.work_date !== conflict.work_date || row.name.replace(/\s+/g, "") !== targetName) return [row];
          if (Number(row.overtime_hours || 0) > 0) return [{ ...row, regular_hours: 0, note: `${row.note ? `${row.note}；` : ""}跨项目核对：本项目仅保留加班` }];
          return [];
        });
        setRows(nextRows);
      }
      const remaining = await checkCrossProjects(nextRows);
      if (!remaining.length && nextRows.length)
        setMode(
          draftSource.startsWith("百度 OCR")
            ? "screenshot"
            : draftSource.startsWith("固定规则")
              ? "rules"
              : "smart",
        );
      else if (!remaining.length)
        setMode("home");
    } catch (error) {
      notify(error instanceof Error ? error.message : "跨项目冲突处理失败", true);
    } finally {
      setRunning("");
    }
  };
  const conflictIndexes = useMemo(() => {
    const conflicts = new Set<number>();
    const draftGroups = new Map<string, {
      indexes: number[]; regular: number; existingDaily: number; existingArea: number;
    }>();
    rows.forEach((row, index) => {
      const person = nameMap.get(row.name.trim());
      const area = row.area.trim();
      const key = `${row.name.trim()}|${row.work_date}|${area}`;
      const existingDaily = person
        ? attendance
            .filter(item => item.person_id === person.id && item.work_date === row.work_date)
            .reduce((sum, item) => sum + Number(item.regular_hours || 0), 0)
        : 0;
      const existingArea = person
        ? attendance
            .filter(item =>
              item.person_id === person.id &&
              item.work_date === row.work_date &&
              String(item.area || "").trim() === area
            )
            .reduce((sum, item) => sum + Number(item.regular_hours || 0), 0)
        : 0;
      const group = draftGroups.get(key) || {
        indexes: [],
        regular: 0,
        existingDaily,
        existingArea,
      };
      group.indexes.push(index);
      group.regular = correctionMode
        ? Number(row.regular_hours || 0)
        : group.regular + Number(row.regular_hours || 0);
      draftGroups.set(key, group);
    });
    const projectedDaily = new Map<string, { total: number; indexes: number[] }>();
    for (const [key, group] of draftGroups) {
      const [name, workDate] = key.split("|");
      const dailyKey = `${name}|${workDate}`;
      const current = projectedDaily.get(dailyKey) || { total: group.existingDaily, indexes: [] };
      current.total += correctionMode ? group.regular - group.existingArea : group.regular;
      current.indexes.push(...group.indexes);
      projectedDaily.set(dailyKey, current);
    }
    for (const group of projectedDaily.values())
      if (group.total > 8.0001)
        group.indexes.forEach(index => conflicts.add(index));
    return conflicts;
  }, [rows, nameMap, attendance, correctionMode]);

  const context = useMemo(() => {
    const personById = new Map(people.map((person) => [person.id, person]));
    return {
      month: date.slice(0, 7),
      participantCount: new Set(attendance.map((row) => row.person_id)).size,
      attendanceCount: attendance.length,
      regularHours: attendance.reduce(
        (sum, row) => sum + Number(row.regular_hours),
        0,
      ),
      overtimeHours: attendance.reduce(
        (sum, row) => sum + Number(row.overtime_hours),
        0,
      ),
      wageExpense: summary.reduce((sum, row) => sum + row.payable, 0),
      people: summary.map((row) => ({
        name: row.name,
        trade: row.trade,
        days: Number(row.days.toFixed(2)),
        regularHours: row.regular,
        overtimeHours: row.overtime,
        payable: Number(row.payable.toFixed(2)),
      })),
      attendance: attendance.map((row) => ({
        name: personById.get(row.person_id)?.name || "未建档人员",
        date: row.work_date,
        area: row.area,
        regularHours: Number(row.regular_hours),
        overtimeHours: Number(row.overtime_hours),
      })),
      deterministicIssues: attendanceCheckProblems(attendance, people),
    };
  }, [attendance, people, summary, date]);

  const chooseImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setRunning("image");
      setImage(await prepareImage(file));
      setImageName(file.name);
    } catch (error) {
      notify(error instanceof Error ? error.message : "截图读取失败", true);
    } finally {
      setRunning("");
      event.target.value = "";
    }
  };

  const recognize = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!image) return notify("请先从相册选择截图或直接拍照", true);
    setRunning("recognize");
    setRows([]);
    setPersonRows([]);
    setSalaryRows([]);
    setWarnings([]);
    setRecognizeError("");
    try {
      const result = await aiRequest<{ rows: AiRow[]; warnings: string[] }>({
        action: "parse_attendance",
        projectName: project.name,
        defaultDate: date,
        image,
        knownNames: people.map((person) => person.name),
      });
      setRows(result.rows || []);
      setWarnings(result.warnings || []);
      setCorrectionMode(false);
      setDraftSource("百度 OCR 截图识别");
      if (!result.rows?.length) {
        const message =
          "截图已经读取，但没有提取到明确考勤，请检查图片或补充默认日期";
        setRecognizeError(message);
        notify(message, true);
      } else {
        notify(`OCR 已识别 ${result.rows.length} 条，请在当前窗口核对后再保存`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "AI 识别失败";
      setRecognizeError(message);
      notify(message, true);
    } finally {
      setRunning("");
    }
  };

  const smartRecognize = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!sourceText.trim()) return notify("请先粘贴考勤文字", true);
    setRunning("smart");
    setRows([]);
    setPersonRows([]);
    setSalaryRows([]);
    setWarnings([]);
    setRecognizeError("");
    try {
      const result = await aiRequest<{ rows: AiRow[]; warnings: string[] }>({
        action: "parse_attendance_ai",
        projectName: project.name,
        defaultDate: date,
        text: sourceText,
        knownNames: people.map((person) => person.name),
      });
      setRows(result.rows || []);
      setWarnings(result.warnings || []);
      const correcting = isCorrectionText(sourceText);
      setCorrectionMode(correcting);
      setDraftSource(correcting ? "Qwen3 更正录入" : "Qwen3 智能录入");
      if (!result.rows?.length) {
        const message = "Qwen3 没有整理出有效考勤，请检查输入文字";
        setRecognizeError(message);
        notify(message, true);
      } else {
        notify(`Qwen3 已整理 ${result.rows.length} 条，请在当前窗口核对后再保存`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "智能录入失败";
      setRecognizeError(message);
      notify(message, true);
    } finally {
      setRunning("");
    }
  };

  const rulesRecognize = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!sourceText.trim()) return notify("请先粘贴考勤文字", true);
    setRunning("rules");
    setRows([]);
    setPersonRows([]);
    setSalaryRows([]);
    setWarnings([]);
    setRecognizeError("");
    try {
      const result = await aiRequest<{ rows: AiRow[]; warnings: string[] }>({
        action: "parse_attendance_rules",
        projectName: project.name,
        defaultDate: date,
        text: sourceText,
        knownNames: people.map((person) => person.name),
      });
      setRows(result.rows || []);
      setWarnings(result.warnings || []);
      const correcting = isCorrectionText(sourceText);
      setCorrectionMode(correcting);
      setDraftSource(correcting ? "固定规则更正录入" : "固定规则录入");
      if (!result.rows?.length) {
        const message = "规则没有整理出有效考勤，请检查格式或改用 AI 录入";
        setRecognizeError(message);
        notify(message, true);
      } else {
        notify(`规则已整理 ${result.rows.length} 条，请核对后再保存`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "规则录入失败";
      setRecognizeError(message);
      notify(message, true);
    } finally {
      setRunning("");
    }
  };

  const recognizePeople = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!peopleText.trim()) return notify("请先粘贴人员信息", true);
    if (
      !window.confirm(
        "人员资料可能包含身份证号、银行卡号和手机号，将把本次粘贴的文字发送给 Qwen3 进行识别。是否继续？",
      )
    )
      return;
    setRunning("people");
    setRows([]);
    setSalaryRows([]);
    setPersonRows([]);
    setWarnings([]);
    setRecognizeError("");
    try {
      const result = await aiRequest<{
        rows: AiPersonRow[];
        warnings: string[];
      }>({
        action: "parse_people_ai",
        projectName: project.name,
        text: peopleText,
        knownNames: people.map((person) => person.name),
      });
      setPersonRows(result.rows || []);
      setWarnings(result.warnings || []);
      setDraftSource("Qwen3 人员信息识别");
      if (!result.rows?.length) {
        const message = "Qwen3 没有整理出有效人员信息，请检查输入文字";
        setRecognizeError(message);
        notify(message, true);
      } else {
        notify(`Qwen3 已整理 ${result.rows.length} 人，请在当前窗口核对后再写入`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "人员信息识别失败";
      setRecognizeError(message);
      notify(message, true);
    } finally {
      setRunning("");
    }
  };

  const recognizeSalary = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!salaryText.trim()) return notify("请先粘贴薪资信息", true);
    setRunning("salary");
    setRows([]);
    setPersonRows([]);
    setSalaryRows([]);
    setWarnings([]);
    setRecognizeError("");
    try {
      const result = await aiRequest<{
        rows: AiSalaryRow[];
        warnings: string[];
      }>({
        action: "parse_salary_ai",
        projectName: project.name,
        text: salaryText,
        knownNames: people.map((person) => person.name),
      });
      setSalaryRows(result.rows || []);
      setWarnings(result.warnings || []);
      setDraftSource("Qwen3 薪资信息识别");
      if (!result.rows?.length) {
        const message = "Qwen3 没有整理出有效薪资信息，请检查输入文字";
        setRecognizeError(message);
        notify(message, true);
      } else {
        notify(`Qwen3 已整理 ${result.rows.length} 人的薪资，请在当前窗口核对后再写入`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "薪资信息识别失败";
      setRecognizeError(message);
      notify(message, true);
    } finally {
      setRunning("");
    }
  };

  const updateRow = (index: number, patch: Partial<AiRow>) =>
    setRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );

  const updatePersonRow = (index: number, patch: Partial<AiPersonRow>) =>
    setPersonRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );

  const updateSalaryRow = (index: number, patch: Partial<AiSalaryRow>) =>
    setSalaryRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );

  const savePersonRows = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!personRows.length) return notify("没有可写入的人员信息", true);
    if (personRows.some((row) => !row.name.trim()))
      return notify("人员姓名不能为空", true);
    const personNames = personRows.map((row) => row.name.trim());
    const duplicateNames = personNames.filter(
      (name, index) => personNames.indexOf(name) !== index,
    );
    if (duplicateNames.length)
      return notify(`识别结果中存在重复姓名：${Array.from(new Set(duplicateNames)).join("、")}`, true);
    if (!window.confirm(`确认把 ${personRows.length} 人的资料写入“${project.name}”吗？`))
      return;
    setSaving(true);
    try {
      let created = 0;
      let updated = 0;
      for (const row of personRows) {
        const name = row.name.replace(/\s/g, "").trim();
        const existing = people.find((person) => person.name.replace(/\s/g, "") === name);
        const recognized = {
          ...(row.trade.trim() ? { trade: row.trade.trim() } : {}),
          ...(row.phone.trim() ? { phone: row.phone.trim() } : {}),
          ...(row.id_no.trim() ? { id_no: row.id_no.trim().toUpperCase() } : {}),
          ...(row.bank_card.trim() ? { bank_card: row.bank_card.replace(/\s/g, "") } : {}),
          ...(row.bank_name.trim() ? { bank_name: row.bank_name.trim() } : {}),
          ...(row.remark.trim() ? { remark: row.remark.trim() } : {}),
        };

        let profileId = existing?.profile_id || "";
        if (!profileId) {
          const profileResult = await supabase
            .from("person_profiles")
            .select("id")
            .eq("owner_id", project.owner_id)
            .eq("canonical_name", name)
            .maybeSingle();
          if (profileResult.error) throw profileResult.error;
          profileId = String(profileResult.data?.id || "");
        }
        if (profileId) {
          const profileResult = await supabase
            .from("person_profiles")
            .update({ name, canonical_name: name, ...recognized })
            .eq("id", profileId)
            .select("id");
          if (profileResult.error) throw profileResult.error;
        } else {
          const profileResult = await supabase
            .from("person_profiles")
            .insert({
              owner_id: project.owner_id,
              canonical_name: name,
              name,
              trade: "",
              phone: "",
              id_no: "",
              bank_card: "",
              bank_name: "",
              salary_type: "日薪",
              rate: 0,
              overtime_rate: 0,
              remark: "",
              active: true,
              ...recognized,
            })
            .select("id")
            .single();
          if (profileResult.error) throw profileResult.error;
          profileId = profileResult.data.id;
        }

        if (existing) {
          if (!existing.profile_id) {
            const membershipResult = await supabase
              .from("people")
              .update({ profile_id: profileId, name })
              .eq("id", existing.id)
              .eq("project_id", project.id);
            if (membershipResult.error) throw membershipResult.error;
          }
          updated += 1;
        } else {
          const { error } = await supabase.from("people").insert({
            project_id: project.id,
            profile_id: profileId,
            name,
            trade: "",
            phone: "",
            id_no: "",
            bank_card: "",
            bank_name: "",
            salary_type: "日薪",
            rate: 0,
            overtime_rate: 0,
            remark: "",
            active: true,
            ...recognized,
          });
          if (error) throw error;
          created += 1;
        }
      }
      notify(`人员资料已写入：新增 ${created} 人，更新 ${updated} 人`);
      setPersonRows([]);
      setPeopleText("");
      setWarnings([]);
      await reload();
      setMode("home");
    } catch (error) {
      notify(error instanceof Error ? error.message : "人员资料写入失败", true);
    } finally {
      setSaving(false);
    }
  };

  const saveSalaryRows = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!salaryRows.length) return notify("没有可写入的薪资信息", true);
    const unknownNames = salaryRows
      .map((row) => row.name.trim())
      .filter((name) => !nameMap.has(name));
    if (unknownNames.length)
      return notify(`以下人员尚未建档，请先修正姓名或录入人员：${unknownNames.join("、")}`, true);
    const salaryNames = salaryRows.map((row) => row.name.trim());
    const duplicateSalaryNames = salaryNames.filter(
      (name, index) => salaryNames.indexOf(name) !== index,
    );
    if (duplicateSalaryNames.length)
      return notify(`薪资结果中存在重复姓名：${Array.from(new Set(duplicateSalaryNames)).join("、")}`, true);
    if (!window.confirm(`确认更新 ${salaryRows.length} 人在“${project.name}”的薪资标准吗？`))
      return;
    setSaving(true);
    try {
      for (const row of salaryRows) {
        const person = nameMap.get(row.name.trim());
        if (!person) continue;
        if (!person.profile_id)
          throw new Error(`“${person.name}”缺少全局人员主档关联，请刷新后重试`);
        const { error } = await supabase
          .from("person_profiles")
          .update({
            salary_type: row.salary_type,
            rate: Math.max(0, Number(row.rate) || 0),
            overtime_rate: Math.max(0, Number(row.overtime_rate) || 0),
          })
          .eq("id", person.profile_id);
        if (error) throw error;
      }
      notify(`已更新 ${salaryRows.length} 人的薪资标准`);
      setSalaryRows([]);
      setSalaryText("");
      setWarnings([]);
      await reload();
      setMode("home");
    } catch (error) {
      notify(error instanceof Error ? error.message : "薪资信息写入失败", true);
    } finally {
      setSaving(false);
    }
  };

  const saveRows = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!rows.length) return notify("没有可保存的识别结果", true);
    if (conflictIndexes.size)
      return notify("同一人员当天正班合计超过 8 小时，请调整正班时间；加班时间不受此限制", true);
    setSaving(true);
    try {
      const cross = await checkCrossProjects(rows);
      if (cross.length) {
        setMode("audit");
        notify(`发现 ${cross.length} 组跨项目正班超过 8 小时，请先选择正班归属项目`, true);
        return;
      }
      const actionText = correctionMode ? "按更正后的最终工时覆盖" : "写入";
      if (!window.confirm(`确认把 ${draftSource || "识别"}生成的 ${rows.length} 条考勤${actionText}到“${project.name}”吗？\n${correctionMode ? "更正名单中未出现的原考勤不会删除。" : ""}`)) return;
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error("登录已过期，请重新登录");
      const response = await fetch("/api/sync", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "attendanceSave",
          project_id: project.id,
          attendance_correction: correctionMode,
          attendance: rows.map((row) => ({
            name: row.name.trim(),
            work_date: row.work_date,
            area: row.area.trim(),
            regular_hours: Number(row.regular_hours),
            overtime_hours: Number(row.overtime_hours),
            remark: `${draftSource || "识别录入"}${row.note ? `：${row.note}` : ""}`,
          })),
        }),
      });
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
        attendance?: number;
        merged?: number;
      };
      if (!response.ok) throw new Error(result.error || "写入云端失败");
      notify(correctionMode
        ? `更正完成：已保存 ${result.attendance ?? rows.length} 条最终考勤，未出现的原考勤未删除`
        : `已保存 ${result.attendance ?? rows.length} 条 AI 考勤${result.merged ? `，其中 ${result.merged} 条已与当天原记录合并` : ""}`);
      setRows([]);
      setSourceText("");
      setImage("");
      setImageName("");
      setCorrectionMode(false);
      await reload();
      setMode("home");
    } catch (error) {
      notify(error instanceof Error ? error.message : "保存失败", true);
    } finally {
      setSaving(false);
    }
  };

  const runReport = async (action: "audit" | "monthly_analysis") => {
    if (!project) return notify("请先选择项目", true);
    if (!attendance.length && action !== "audit") return notify("当前月份没有考勤数据", true);
    setRunning(action);
    try {
      const conflicts = action === "audit" ? await checkCrossProjects() : [];
      const crossIssues = conflicts.map(conflict =>
        `跨项目冲突：${conflict.name} ${conflict.work_date} 在 ${Array.from(new Set(conflict.entries.filter(entry => entry.regular_hours > 0).map(entry => entry.project_name))).join("、")} 录入正班，合计 ${conflict.total_regular} 小时`,
      );
      const result = await aiRequest<{ answer: string }>({
        action,
        projectName: project.name,
        month: date.slice(0, 7),
        context: action === "audit"
          ? { ...context, deterministicIssues: [...context.deterministicIssues, ...crossIssues] }
          : context,
      });
      if (action === "audit") setAudit(result.answer);
      else setAnalysis(result.answer);
      setMode(action === "audit" ? "audit" : "home");
    } catch (error) {
      notify(error instanceof Error ? error.message : "AI 分析失败", true);
    } finally {
      setRunning("");
    }
  };

  const ask = async () => {
    if (!project) return notify("请先选择项目", true);
    if (!question.trim()) return notify("请输入问题", true);
    setRunning("ask");
    try {
      const result = await aiRequest<{ answer: string }>({
        action: "ask",
        projectName: project.name,
        month: date.slice(0, 7),
        question,
        context,
      });
      setAnswer(result.answer);
      setMode("home");
    } catch (error) {
      notify(error instanceof Error ? error.message : "AI 问答失败", true);
    } finally {
      setRunning("");
    }
  };

  return (
    <div className="ai-page">
      <div className="ai-feature-grid">
        <button className={mode === "screenshot" ? "active" : ""} type="button" onClick={() => setMode("screenshot")}><b>截图识别</b><span>百度 OCR · 相册或拍照</span></button>
        <button className={mode === "rules" ? "active" : ""} type="button" onClick={() => setMode("rules")}><b>规则录入</b><span>固定规则 · 快速稳定</span></button>
        <button className={mode === "smart" ? "active" : ""} type="button" onClick={() => setMode("smart")}><b>AI 录入</b><span>Qwen3 · 自由格式理解</span></button>
        <button className={mode === "people" ? "active" : ""} type="button" onClick={() => setMode("people")}><b>人员信息识别</b><span>Qwen3 · 姓名与证件资料</span></button>
        <button className={mode === "salary" ? "active" : ""} type="button" onClick={() => setMode("salary")}><b>薪资信息识别</b><span>Qwen3 · 日薪月薪与加班单价</span></button>
        <button className={mode === "audit" ? "active" : ""} type="button" onClick={() => setMode("audit")}><b>异常核对</b><span>跨项目重复、工时与姓名</span></button>
        <button className={mode === "analysis" ? "active" : ""} type="button" onClick={() => setMode("analysis")}><b>分析问答</b><span>Qwen3 · 月报总结与查询</span></button>
      </div>

      {mode === "screenshot" && !rows.length && <section className="card ai-recognizer ai-mode-panel">
        <div className="card-head">
          <div>
            <h2>截图识别</h2>
            <p>仅此功能调用百度 OCR；图片不会保存到数据库</p>
          </div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body ai-input-grid">
          <div>
            <label className="block ai-date">
              默认日期
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </label>
            <p className="muted ai-mode-tip">适合微信截图、相册图片和现场拍照。识别完成后会自动回到主页显示明细。</p>
          </div>
          <div className="ai-upload">
            <div className="ai-upload-options">
              <label className="ai-upload-button gallery">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => void chooseImage(event)}
                />
                <b>{running === "image" ? "正在处理图片…" : "从手机相册选择"}</b>
                <span>选择微信截图或已有照片</span>
              </label>
              <label className="ai-upload-button camera">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(event) => void chooseImage(event)}
                />
                <b>直接拍照识别</b>
                <span>调用手机后置摄像头</span>
              </label>
            </div>
            {image ? (
              <div className="ai-image-preview">
                <img src={image} alt="待识别考勤截图" />
                <span>{imageName}</span>
                <button
                  className="danger"
                  onClick={() => {
                    setImage("");
                    setImageName("");
                  }}
                >
                  移除截图
                </button>
              </div>
            ) : (
              <p className="muted">可直接选择手机相册中的微信截图</p>
            )}
          </div>
        </div>
        <div className="ai-main-action">
          <button
            className="primary"
            disabled={Boolean(running)}
            onClick={() => void recognize()}
          >
            {running === "recognize" ? "OCR 正在识别…" : "OCR 识别考勤"}
          </button>
        </div>
        {recognizeError && (
          <div className="ai-recognize-error">
            <b>识别失败原因：</b>{recognizeError}
          </div>
        )}
      </section>}

      {mode === "rules" && !rows.length && <section className="card ai-mode-panel">
        <div className="card-head">
          <div><h2>规则录入</h2><p>只按固定考勤规则解析，速度快且不调用 AI</p></div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body">
          <label className="block ai-date">默认日期<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label className="block">考勤文字<textarea rows={12} value={sourceText} onChange={(event) => setSourceText(event.target.value)} placeholder="例如：&#10;7月22日3号路西考勤&#10;叶海颜一天，晚上加班1小时" /></label>
          <p className="muted ai-mode-tip">适合“姓名＋全天/半天＋加班小时”等固定格式；复杂或不规则内容请使用 AI 录入。</p>
          <div className="ai-main-action"><button className="primary" disabled={Boolean(running)} onClick={() => void rulesRecognize()}>{running === "rules" ? "规则正在整理…" : "按规则整理"}</button></div>
        </div>
        {recognizeError && <div className="ai-recognize-error"><b>录入失败原因：</b>{recognizeError}</div>}
      </section>}

      {mode === "smart" && !rows.length && <section className="card ai-mode-panel">
        <div className="card-head">
          <div><h2>Qwen3 AI 录入</h2><p>由 Qwen3 独立理解自由格式，不使用固定规则覆盖结果</p></div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body">
          <label className="block ai-date">默认日期<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label className="block">考勤文字<textarea rows={12} value={sourceText} onChange={(event) => setSourceText(event.target.value)} placeholder="例如：&#10;2026年7月18日 3号路考勤&#10;陈香平1天&#10;车环洋下午半天" /></label>
          <div className="ai-main-action"><button className="primary" disabled={Boolean(running)} onClick={() => void smartRecognize()}>{running === "smart" ? "Qwen3 正在整理…" : "Qwen3 智能整理"}</button></div>
        </div>
        {recognizeError && <div className="ai-recognize-error"><b>录入失败原因：</b>{recognizeError}</div>}
      </section>}

      {mode === "people" && !personRows.length && <section className="card ai-mode-panel">
        <div className="card-head">
          <div><h2>Qwen3 人员信息识别</h2><p>不限制排版，以姓名作为每条人员资料的起点，智能归集证件和银行信息</p></div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body">
          <div className="ai-sensitive-tip"><b>敏感资料提示：</b>点击识别后，本次粘贴的文字会发送给 Qwen3，可能包含身份证号、银行卡号和手机号；系统会再次询问确认。</div>
          <label className="block">人员信息<textarea rows={13} value={peopleText} onChange={(event) => setPeopleText(event.target.value)} placeholder="格式不限。例如：&#10;张三 330... 手机138... 农业银行杭州某支行 622...&#10;李四，身份证……，电话……，银行卡……" /></label>
          <p className="muted ai-mode-tip">姓名、身份证、手机号、银行卡、开户行和工种均可在识别结果中修改；无法确定归属的内容不会猜测写入。</p>
          <div className="ai-main-action"><button className="primary" disabled={Boolean(running)} onClick={() => void recognizePeople()}>{running === "people" ? "Qwen3 正在整理…" : "识别人员信息"}</button></div>
        </div>
        {recognizeError && <div className="ai-recognize-error"><b>识别失败原因：</b>{recognizeError}</div>}
      </section>}

      {mode === "salary" && !salaryRows.length && <section className="card ai-mode-panel">
        <div className="card-head">
          <div><h2>Qwen3 薪资信息识别</h2><p>识别姓名、日薪或月薪、工资单价和每小时加班单价</p></div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body">
          <label className="block">薪资信息<textarea rows={12} value={salaryText} onChange={(event) => setSalaryText(event.target.value)} placeholder="格式不限。例如：&#10;张三每天350元，加班每小时45元&#10;李四月薪9000，加班一小时50" /></label>
          <p className="muted ai-mode-tip">只更新当前项目已建档人员。AI结果可修改，姓名不匹配时会阻止写入。</p>
          <div className="ai-main-action"><button className="primary" disabled={Boolean(running)} onClick={() => void recognizeSalary()}>{running === "salary" ? "Qwen3 正在整理…" : "识别薪资信息"}</button></div>
        </div>
        {recognizeError && <div className="ai-recognize-error"><b>识别失败原因：</b>{recognizeError}</div>}
      </section>}

      {(mode === "screenshot" || mode === "rules" || mode === "smart") && rows.length > 0 && (
        <section className="card ai-results ai-mode-panel">
          <div className="card-head">
            <div>
              <h2>识别结果确认</h2>
              <p>{draftSource || "识别结果"} · 姓名、日期、区域和工时均可修改</p>
            </div>
            <div className="ai-result-actions">
              <span className={`pill ${conflictIndexes.size ? "pill-bad" : ""}`}>{conflictIndexes.size ? `${conflictIndexes.size} 条冲突` : `${rows.length} 条草稿`}</span>
              <button className="secondary" type="button" onClick={() => { setRows([]); setWarnings([]); setMode("home"); }}>返回 AI 主页</button>
            </div>
          </div>
          {warnings.length > 0 && (
            <div className="ai-warnings">
              {warnings.map((warning, index) => (
                <span key={index}>{warning}</span>
              ))}
            </div>
          )}
          <div className="ai-row-list">
            {rows.map((row, index) => (
              <div
                className={`ai-edit-row ${conflictIndexes.has(index) ? "conflict" : ""}`}
                key={index}
              >
                <label>姓名<input value={row.name} onChange={(event) => updateRow(index, { name: event.target.value })} /></label>
                <label>日期<input type="date" value={row.work_date} onChange={(event) => updateRow(index, { work_date: event.target.value })} /></label>
                <label>区域<input value={row.area} onChange={(event) => updateRow(index, { area: event.target.value })} /></label>
                <label>正班小时<input type="number" min="0" max="8" step="0.5" value={row.regular_hours} onChange={(event) => updateRow(index, { regular_hours: Number(event.target.value) })} /></label>
                <label>加班小时<input type="number" min="0" max="24" step="0.5" value={row.overtime_hours} onChange={(event) => updateRow(index, { overtime_hours: Number(event.target.value) })} /></label>
                <div className="ai-row-state">
                  <em className={nameMap.has(row.name.trim()) ? "ok" : "warn"}>{nameMap.has(row.name.trim()) ? "已建档" : "新人员"}</em>
                  <small>可信度 {Math.round(row.confidence * 100)}%</small>
                  {conflictIndexes.has(index) && <small className="bad-text">当天各区域正班累计超过 8 小时（加班不计入）</small>}
                  <button className="danger" onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}>删除</button>
                </div>
                {row.note && <p>{row.note}</p>}
              </div>
            ))}
          </div>
          <div className="ai-save-bar">
            <span>AI 可能识别错误，请确认后写入</span>
            <button className="primary" disabled={saving || !rows.length || Boolean(conflictIndexes.size)} onClick={() => void saveRows()}>
              {saving ? "正在写入…" : "确认写入云端"}
            </button>
          </div>
        </section>
      )}

      {mode === "people" && personRows.length > 0 && (
        <section className="card ai-results ai-mode-panel">
          <div className="card-head">
            <div><h2>人员信息识别结果确认</h2><p>{draftSource} · 所有字段均可修改，确认后才写入</p></div>
            <div className="ai-result-actions"><span className="pill">{personRows.length} 人</span><button className="secondary" type="button" onClick={() => { setPersonRows([]); setWarnings([]); setMode("home"); }}>返回 AI 主页</button></div>
          </div>
          {warnings.length > 0 && <div className="ai-warnings">{warnings.map((warning, index) => <span key={index}>{warning}</span>)}</div>}
          <div className="ai-row-list">
            {personRows.map((row, index) => (
              <div className="ai-edit-row ai-person-edit-row" key={index}>
                <label>姓名<input value={row.name} onChange={(event) => updatePersonRow(index, { name: event.target.value })} /></label>
                <label>身份证号码<input value={row.id_no} onChange={(event) => updatePersonRow(index, { id_no: event.target.value })} /></label>
                <label>手机号码<input value={row.phone} onChange={(event) => updatePersonRow(index, { phone: event.target.value })} /></label>
                <label>银行卡号码<input value={row.bank_card} onChange={(event) => updatePersonRow(index, { bank_card: event.target.value })} /></label>
                <label>开户行<input value={row.bank_name} onChange={(event) => updatePersonRow(index, { bank_name: event.target.value })} /></label>
                <label>工种<input value={row.trade} onChange={(event) => updatePersonRow(index, { trade: event.target.value })} /></label>
                <label>备注<input value={row.remark} onChange={(event) => updatePersonRow(index, { remark: event.target.value })} /></label>
                <div className="ai-row-state">
                  <em className={nameMap.has(row.name.trim()) ? "ok" : "warn"}>{nameMap.has(row.name.trim()) ? "更新已有" : "新增人员"}</em>
                  <small>可信度 {Math.round(row.confidence * 100)}%</small>
                  <button className="danger" onClick={() => setPersonRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}>删除</button>
                </div>
              </div>
            ))}
          </div>
          <div className="ai-save-bar"><span>请逐项核对；已有人员的空白字段不会覆盖原资料</span><button className="primary" disabled={saving || !personRows.length} onClick={() => void savePersonRows()}>{saving ? "正在写入…" : "确认写入人员资料"}</button></div>
        </section>
      )}

      {mode === "salary" && salaryRows.length > 0 && (
        <section className="card ai-results ai-mode-panel">
          <div className="card-head">
            <div><h2>薪资信息识别结果确认</h2><p>{draftSource} · 姓名、薪资类型和单价均可修改</p></div>
            <div className="ai-result-actions"><span className="pill">{salaryRows.length} 人</span><button className="secondary" type="button" onClick={() => { setSalaryRows([]); setWarnings([]); setMode("home"); }}>返回 AI 主页</button></div>
          </div>
          {warnings.length > 0 && <div className="ai-warnings">{warnings.map((warning, index) => <span key={index}>{warning}</span>)}</div>}
          <div className="ai-row-list">
            {salaryRows.map((row, index) => (
              <div className="ai-edit-row ai-salary-edit-row" key={index}>
                <label>姓名<input value={row.name} onChange={(event) => updateSalaryRow(index, { name: event.target.value })} /></label>
                <label>薪资类型<select value={row.salary_type} onChange={(event) => updateSalaryRow(index, { salary_type: event.target.value as "日薪" | "月薪" })}><option value="日薪">日薪</option><option value="月薪">月薪</option></select></label>
                <label>工资单价<input type="number" min="0" step="0.01" value={row.rate} onChange={(event) => updateSalaryRow(index, { rate: Number(event.target.value) })} /></label>
                <label>加班单价/小时<input type="number" min="0" step="0.01" value={row.overtime_rate} onChange={(event) => updateSalaryRow(index, { overtime_rate: Number(event.target.value) })} /></label>
                <label>识别备注<input value={row.note} onChange={(event) => updateSalaryRow(index, { note: event.target.value })} /></label>
                <div className="ai-row-state">
                  <em className={nameMap.has(row.name.trim()) ? "ok" : "warn"}>{nameMap.has(row.name.trim()) ? "已匹配" : "未建档"}</em>
                  <small>可信度 {Math.round(row.confidence * 100)}%</small>
                  <button className="danger" onClick={() => setSalaryRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}>删除</button>
                </div>
              </div>
            ))}
          </div>
          <div className="ai-save-bar"><span>未建档姓名会阻止写入，请先修正或录入人员</span><button className="primary" disabled={saving || !salaryRows.length} onClick={() => void saveSalaryRows()}>{saving ? "正在写入…" : "确认更新薪资标准"}</button></div>
        </section>
      )}

      {mode === "audit" && <section className="card ai-mode-panel">
        <div className="card-head">
          <div><h2>跨项目异常核对</h2><p>先对账号内所有项目执行确定性规则，再由 Qwen3 补充分析</p></div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body ai-mode-action">
          <p>当前项目：{project?.name || "未选择"} · 审查月份：{date.slice(0, 7)} · 审查范围：账号内全部项目</p>
          <button className="primary" disabled={Boolean(running)} onClick={() => void runReport("audit")}>{running === "audit" ? "Qwen3 正在核对…" : "开始异常核对"}</button>
        </div>
        {crossConflicts.length > 0 && <div className="cross-audit-list">
          <div className="cross-audit-summary">发现 {crossConflicts.length} 组跨项目正班超过 8 小时。请为每组选择正班应归属的项目；其他项目的仅加班工时会保留。</div>
          {crossConflicts.map(conflict => {
            const projectEntries = Array.from(new Map(conflict.entries.filter(entry => entry.regular_hours > 0).map(entry => [entry.project_id, entry])).values());
            return <div className="cross-audit-item" key={conflict.key}>
              <div className="cross-audit-title"><b>{conflict.name}</b><span>{conflict.work_date}</span><em>正班合计 {conflict.total_regular} 小时</em></div>
              <div className="cross-audit-entries">{conflict.entries.map((entry, index) => <span key={`${entry.attendance_id || entry.project_id}-${index}`}><b>{entry.project_name}</b>：正班 {entry.regular_hours}h，加班 {entry.overtime_hours}h</span>)}</div>
              <div className="cross-audit-resolve">
                <label>正班归属项目<select value={crossChoices[conflict.key] || ""} onChange={event => setCrossChoices(current => ({ ...current, [conflict.key]: event.target.value }))}>{projectEntries.map(entry => <option key={entry.project_id} value={entry.project_id}>{entry.project_name}</option>)}</select></label>
                <button className="primary" disabled={Boolean(running)} onClick={() => void resolveCrossConflict(conflict)}>{running === `resolve:${conflict.key}` ? "正在处理…" : "确认归属并修改"}</button>
              </div>
            </div>;
          })}
        </div>}
        {audit && <div className="card-body"><div className="ai-answer">{audit}</div></div>}
      </section>}

      {mode === "analysis" && <section className="card ai-mode-panel">
        <div className="card-head">
          <div><h2>Qwen3 分析问答</h2><p>月度总结和自由问答集中在一个功能页</p></div>
          <button className="secondary ai-back" type="button" onClick={() => setMode("home")}>返回 AI 主页</button>
        </div>
        <div className="card-body ai-analysis-actions">
          <div className="ai-analysis-box">
            <h3>月度分析</h3>
            <p>自动总结人员、工时、工资支出和异常风险。</p>
            <button className="primary" disabled={Boolean(running)} onClick={() => void runReport("monthly_analysis")}>{running === "monthly_analysis" ? "Qwen3 正在分析…" : "生成月度分析"}</button>
          </div>
          <div className="ai-analysis-box">
            <h3>数据问答</h3>
            <p>询问当前项目和当前月份已有数据。</p>
            <div className="ai-question-row">
              <input value={question} onChange={(event) => setQuestion(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void ask(); }} placeholder="例如：本月谁的加班最多？工资支出是多少？" />
              <button className="primary" disabled={Boolean(running)} onClick={() => void ask()}>{running === "ask" ? "Qwen3 回答中…" : "提问"}</button>
            </div>
          </div>
        </div>
      </section>}

      {mode === "home" && audit && <section className="card ai-home-result"><div className="card-head"><div><h2>异常核对结果</h2><p>Qwen3 · {date.slice(0, 7)}</p></div><button className="secondary" type="button" onClick={() => setMode("audit")}>重新核对</button></div><div className="card-body"><div className="ai-answer">{audit}</div></div></section>}
      {mode === "home" && analysis && <section className="card ai-home-result"><div className="card-head"><div><h2>月度分析结果</h2><p>Qwen3 · {date.slice(0, 7)}</p></div><button className="secondary" type="button" onClick={() => setMode("analysis")}>重新分析</button></div><div className="card-body"><div className="ai-answer">{analysis}</div></div></section>}
      {mode === "home" && answer && <section className="card ai-home-result"><div className="card-head"><div><h2>问答结果</h2><p>{question}</p></div><button className="secondary" type="button" onClick={() => setMode("analysis")}>继续提问</button></div><div className="card-body"><div className="ai-answer">{answer}</div></div></section>}

      {mode === "home" && !rows.length && !personRows.length && !salaryRows.length && !audit && !analysis && !answer && <section className="card ai-home-welcome"><div className="card-body"><h2>请选择一个录入或 AI 功能</h2><p>规则录入不调用 AI；截图识别使用百度 OCR；AI录入、人员信息、薪资信息、异常核对和分析问答使用 Qwen3 30B。</p></div></section>}
      <p className="ai-privacy">考勤截图不会保存到数据库。人员信息识别仅在您确认后，把本次粘贴的文字发送给 Qwen3；请务必在写入前核对敏感资料。</p>
    </div>
  );
}
